import React from "react";
import { Box, Typography } from "@mui/material";
import { useMetricsUi, MONO_FONT } from "./metricsUi";

/**
 * Reference-style "Calculation reference" footnote. Renders only the
 * definitions the API actually reports (sales_definition + audit) —
 * nothing is invented here.
 */
const CalculationFootnote = ({ salesDefinition, audit }) => {
  const ui = useMetricsUi();
  if (!salesDefinition && !audit) return null;

  const formulas = [
    salesDefinition?.description && {
      name: "Gross Sales",
      text: salesDefinition.description,
    },
    {
      name: "Gross AOV",
      text: "Gross Sales ÷ Orders",
      hint: "pre-returns, pre-discount order value",
    },
    {
      name: "UPT",
      text: "Units Sold ÷ Orders",
      hint: "units per transaction",
    },
    {
      name: "Inventory On Hand",
      text: "Live in-hand units across warehouses",
      hint: "current snapshot — not period-end stock; not scoped by order source",
    },
  ].filter(Boolean);

  return (
    <Box
      sx={{
        mt: 4,
        pt: 2.5,
        borderTop: `1px solid ${ui.colors.border}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 700,
          color: ui.colors.textTertiary,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 1.25,
        }}
      >
        Calculation reference
      </Typography>

      {formulas.map((formula) => (
        <Typography
          key={formula.name}
          sx={{
            fontSize: 12,
            color: ui.colors.textSecondary,
            lineHeight: 1.7,
          }}
        >
          <Box
            component="span"
            sx={{ color: ui.colors.textPrimary, fontWeight: 600 }}
          >
            {formula.name}
          </Box>
          {" — "}
          {formula.text}
          {formula.hint && (
            <Box component="span" sx={{ color: ui.colors.textTertiary }}>
              {" "}
              — {formula.hint}
            </Box>
          )}
        </Typography>
      ))}

      {audit && (
        <Typography
          sx={{
            mt: 1.5,
            fontSize: 11,
            color: ui.colors.textTertiary,
            fontFamily: MONO_FONT,
          }}
        >
          Excludes {audit.exclusions?.join(", ") || "—"} · date field{" "}
          {audit.date_field} ({audit.date_bucket}) · price {audit.price_field} ·
          generated {audit.generated_at}
        </Typography>
      )}
    </Box>
  );
};

export default CalculationFootnote;
